/**
 * Pieces shared by Clients → All clients, the client panel and its pop-ups: the client logo (prototype
 * `logo()`), the logo with its health dot (prototype `hdot()`), and a PATCH that always carries the
 * client's current `version` (ClientRow has none, so it is read fresh from GET /clients/{id} first).
 */
import { useQueryClient } from '@tanstack/react-query';
import type { ClientRow, HealthBand, UpdateClientBody } from '@amplispot/contracts';
import { ApiFailure } from '../../api';
import { keys, useUpdateClient } from '../../lib/data';
import { logoColour, logoInitials } from '../../lib/format';
import { bandInfo, errorText, useToast } from '../../ui/kit';

/** Prototype `logo(c, sz)`: a rounded square in the client's colour with its initials. */
export function ClientLogo({ id, name, size = 40 }: { id: string; name: string; size?: number }) {
  return (
    <span
      className="clogo"
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: Math.round(size / 4),
        background: logoColour(id),
        fontSize: Math.round(size * 0.375),
      }}
    >
      {logoInitials(name)}
    </span>
  );
}

/** Prototype `hdot()`: the logo at 26 px with the health dot on its corner (grey until a score exists). */
export function LogoWithDot({ id, name, band }: { id: string; name: string; band: HealthBand | null }) {
  const info = band ? bandInfo(band) : null;
  return (
    <span style={{ position: 'relative', display: 'inline-flex', flex: 'none' }}>
      <ClientLogo id={id} name={name} size={26} />
      <span
        className={`dot ${info ? info.cls : 'mute'}`}
        title={info ? info.label : 'No health score yet'}
        style={{ position: 'absolute', right: -3, bottom: -3, border: '2px solid var(--surface)', width: 11, height: 11, boxSizing: 'border-box', margin: 0 }}
      />
    </span>
  );
}

/** Colour of a card's top edge: the band colour, or the muted line colour while there is no score. */
export const bandEdge = (band: HealthBand | null): string => (band ? bandInfo(band).colour : 'var(--mute)');

/** Prototype meter colour: under 40 cold, under 60 warm, else hot. */
export const meterTone = (u: number): 'cold' | 'warm' | 'hot' => (u < 40 ? 'cold' : u < 60 ? 'warm' : 'hot');

/**
 * PATCH /clients/{id} with If-Match set to the version of the row the manager is looking at (ADR-001 §11.1).
 * The version is deliberately NOT re-read first: a fresh read would always match and silently overwrite
 * another manager's save. If the row is stale the server answers 412: the lists reload and the toast says so.
 */
export function useClientPatch() {
  const qc = useQueryClient();
  const update = useUpdateClient();
  const toast = useToast();

  return async (client: Pick<ClientRow, 'id' | 'name' | 'version'>, body: UpdateClientBody, done: string): Promise<boolean> => {
    try {
      await update.mutateAsync({ id: client.id, version: client.version, body });
      toast(done);
      return true;
    } catch (e) {
      if (e instanceof ApiFailure && e.status === 412) {
        void qc.invalidateQueries({ queryKey: keys.clients });
        void qc.invalidateQueries({ queryKey: keys.client(client.id) });
        toast(`Someone else changed ${client.name} a moment ago. It now shows their change. Make yours again.`);
      } else toast(errorText(e));
      return false;
    }
  };
}
